'use client';

// ─────────────────────────────────────────────────────────────────────────────
// TodayTab — landing tab for the PM dashboard.
//
// Composes morning-routine widgets in priority order:
//   1. ClockInBar  — clock the field PM in for the day
//   2. WeatherAlertBanner — site-impacting weather first
//   3. PMSmartPickSummary — AI briefing covering Jobs / Crew / Materials / QC
//   4. StatsBar — per-PM job counts (the "Your Jobs" strip)
//   5. Cross-tab quick links — Inspections / Tickets jump CTAs
//
// No data fetches owned here. Each composed widget owns its own fetch and
// realtime subscription; the orchestrator keeps all tab panels mounted so
// subscriptions never re-handshake when the user switches tabs.
// ─────────────────────────────────────────────────────────────────────────────

import Icon from '@/components/ui/AppIcon';
import WeatherAlertBanner from '../WeatherAlertBanner';
import PMSmartPickSummary from '../PMSmartPickSummary';
import StatsBar from '../StatsBar';
import type { TabKey } from '../BottomTabBar';

interface TodayTabProps {
  onSwitchTab: (key: TabKey) => void;
}

export default function TodayTab({ onSwitchTab }: TodayTabProps) {
  return (
    <div className="space-y-5">
      <WeatherAlertBanner />
      <PMSmartPickSummary />
      <StatsBar />

      {/* Cross-tab quick CTAs. From Today, the two highest-value jumps are
          (a) start the inspection round, and (b) check what's escalating. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <button
          onClick={() => onSwitchTab('inspections')}
          className="flex items-center justify-between bg-card border border-border rounded-xl px-5 hover:border-primary hover:shadow-sm transition-standard press-scale"
          style={{ minHeight: '64px' }}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(7,176,233,0.1)' }}>
              <Icon name="ClipboardDocumentCheckIcon" size={20} style={{ color: '#07B0E9' }} variant="solid" />
            </div>
            <div className="text-left">
              <p className="font-body text-sm font-semibold text-foreground">Start Inspection Route</p>
              <p className="font-caption text-xs text-muted-foreground">Open today's map and inspection list</p>
            </div>
          </div>
          <Icon name="ArrowRightIcon" size={16} className="text-muted-foreground" />
        </button>

        <button
          onClick={() => onSwitchTab('tickets')}
          className="flex items-center justify-between bg-card border border-border rounded-xl px-5 hover:border-primary hover:shadow-sm transition-standard press-scale"
          style={{ minHeight: '64px' }}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(239,68,68,0.1)' }}>
              <Icon name="ExclamationTriangleIcon" size={20} style={{ color: '#EF4444' }} variant="solid" />
            </div>
            <div className="text-left">
              <p className="font-body text-sm font-semibold text-foreground">Open Tickets & Escalations</p>
              <p className="font-caption text-xs text-muted-foreground">Triage anything routed to you</p>
            </div>
          </div>
          <Icon name="ArrowRightIcon" size={16} className="text-muted-foreground" />
        </button>
      </div>
    </div>
  );
}
